/**
 * Сумма прописью на русском — `01-BUSINESS-RULES.md` §2.7.
 *
 * Полноценный алгоритм числительных, а не заглушка: обрабатывает роды
 * («одна тысяча», но «один рубль»), склонения по количеству («один рубль» /
 * «два рубля» / «пять рублей»), тысячи, миллионы, миллиарды и копейки отдельно.
 *
 * Проверен на реальном числе из документа лаборатории:
 *   42 660 ₽ → «Сорок две тысячи шестьсот шестьдесят рублей ноль копеек»
 *
 * Отличие от демо: на вход подаются целые КОПЕЙКИ, а не рубли с плавающей
 * точкой. В демо копейки вычислялись как `Math.round((amount - rub) * 100)`,
 * что для сумм вида 3200.07 могло дать 6 из-за представления double.
 */
const ONES_M = ['', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'];
const ONES_F = ['', 'одна', 'две', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'];
const TEENS = [
    'десять', 'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать',
    'пятнадцать', 'шестнадцать', 'семнадцать', 'восемнадцать', 'девятнадцать',
];
const TENS = ['', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят', 'восемьдесят', 'девяносто'];
const HUNDREDS = ['', 'сто', 'двести', 'триста', 'четыреста', 'пятьсот', 'шестьсот', 'семьсот', 'восемьсот', 'девятьсот'];
/** Тройка разрядов (0..999) прописью. `fem` — женский род для тысяч и копеек. */
function triple(n, fem) {
    const words = [];
    const h = Math.floor(n / 100);
    const t = Math.floor((n % 100) / 10);
    const o = n % 10;
    if (h)
        words.push(HUNDREDS[h]);
    if (t === 1) {
        words.push(TEENS[o]);
    }
    else {
        if (t)
            words.push(TENS[t]);
        if (o)
            words.push((fem ? ONES_F : ONES_M)[o]);
    }
    return words.join(' ');
}
/**
 * Выбор формы существительного по количеству.
 * `forms` = [для 1, для 2–4, для 5–20 и прочих].
 * Числа 11–14 — исключение: «одиннадцать рублЕЙ», а не «рубль».
 */
export function pluralRu(n, forms) {
    const n100 = n % 100;
    const n10 = n % 10;
    if (n100 >= 11 && n100 <= 14)
        return forms[2];
    if (n10 === 1)
        return forms[0];
    if (n10 >= 2 && n10 <= 4)
        return forms[1];
    return forms[2];
}
const SCALES = [
    { div: 1_000_000_000, forms: ['миллиард', 'миллиарда', 'миллиардов'], fem: false },
    { div: 1_000_000, forms: ['миллион', 'миллиона', 'миллионов'], fem: false },
    { div: 1_000, forms: ['тысяча', 'тысячи', 'тысяч'], fem: true },
];
/** Целое число прописью, без единиц измерения. `fem` — род последней тройки. */
export function integerToWordsRu(value, fem = false) {
    let n = Math.floor(Math.abs(value));
    if (n === 0)
        return 'ноль';
    const parts = [];
    for (const scale of SCALES) {
        const count = Math.floor(n / scale.div);
        if (count > 0) {
            const words = triple(count, scale.fem);
            if (words)
                parts.push(words);
            parts.push(pluralRu(count, scale.forms));
            n %= scale.div;
        }
    }
    const last = triple(n, fem);
    if (last)
        parts.push(last);
    return parts.join(' ');
}
/**
 * Сумма прописью для документов.
 *
 * @param kop Сумма в целых копейках.
 * @returns Например: «Сорок две тысячи шестьсот шестьдесят рублей ноль копеек».
 *          Первая буква заглавная — так в документах лаборатории.
 */
export function kopToWordsRu(kop) {
    const total = Math.round(Math.abs(kop));
    const rub = Math.floor(total / 100);
    const kopecks = total % 100;
    const rubWords = integerToWordsRu(rub, false);
    const rubForm = pluralRu(rub, ['рубль', 'рубля', 'рублей']);
    const kopWords = kopecks > 0 ? triple(kopecks, true) : 'ноль';
    const kopForm = pluralRu(kopecks, ['копейка', 'копейки', 'копеек']);
    const capitalized = rubWords.charAt(0).toUpperCase() + rubWords.slice(1);
    const sign = kop < 0 ? 'минус ' : '';
    return `${sign}${capitalized} ${rubForm} ${kopWords} ${kopForm}`;
}
/**
 * Сумма прописью с копейками ЦИФРАМИ: «Два миллиона пятьсот тысяч рублей 00 копеек».
 *
 * Так набрана строка в новом эталоне акта. Отличие от `kopToWordsRu` только
 * в копейках: рубли по-прежнему словами, а копейки — двумя цифрами. Рядом
 * с суммой «2 500 000,00» словесное «ноль копеек» выглядит непоследовательно.
 */
export function kopToWordsRuDigits(kop) {
    const total = Math.round(Math.abs(kop));
    const rub = Math.floor(total / 100);
    const kopecks = total % 100;
    const rubWords = integerToWordsRu(rub, false);
    const rubForm = pluralRu(rub, ['рубль', 'рубля', 'рублей']);
    const kopForm = pluralRu(kopecks, ['копейка', 'копейки', 'копеек']);
    const capitalized = rubWords.charAt(0).toUpperCase() + rubWords.slice(1);
    const sign = kop < 0 ? 'минус ' : '';
    const digits = String(kopecks).padStart(2, '0');
    return `${sign}${capitalized} ${rubForm} ${digits} ${kopForm}`;
}
/** Удобная обёртка для сумм в рублях. Дробная часть трактуется как копейки. */
export function rublesToWordsRu(rubles) {
    return kopToWordsRu(Math.round(rubles * 100));
}
//# sourceMappingURL=number-to-words-ru.js.map